import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useSessionTimeout } from './hooks/useSessionTimeout'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import Pickups from './pages/Pickups'
import Assignments from './pages/Assignments'
import Reports from './pages/Reports'
import Analytics from './pages/Analytics'
import Collectors from './pages/Collectors'
import Companies from './pages/Companies'
import CompanyPortal from './pages/CompanyPortal'
import Payments from './pages/Payments'
import Donations from './pages/Donations'
import Notifications from './pages/Notifications'
import Subscriptions from './pages/Subscriptions'
import Ratings from './pages/Ratings'
import Payouts from './pages/Payouts'
import Support from './pages/Support'
import AuditLog from './pages/AuditLog'
import SystemHealth from './pages/SystemHealth'
import Settings from './pages/Settings'
import MapView from './pages/MapView'
import NotFound from './pages/NotFound'
import ChangePassword from './pages/ChangePassword'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const mustChangePassword = useAuthStore((s) => s.user?.mustChangePassword)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  // The API refuses everything but the rotation itself while this is set, so
  // sending the user anywhere else would only show them a wall of 403s.
  if (mustChangePassword) return <Navigate to="/change-password" replace />
  return <>{children}</>
}

/**
 * Keep a company operator out of the platform's own pages.
 *
 * The API already refuses them — a COMPANY_ADMIN is denied `/api/admin/*`
 * outright — so this is not the security boundary. It is here so the operator
 * gets an honest "not yours" instead of a page that renders and then fills with
 * error toasts, and so a page added later cannot quietly appear in their shell.
 */
const COMPANY_ADMIN_ROUTES = new Set(['/', '/company', '/settings'])

function RoleRoute({ path, children }: { path: string; children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role)
  if (role === 'COMPANY_ADMIN' && !COMPANY_ADMIN_ROUTES.has(path)) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/**
 * The panel serves two audiences from one shell. A company admin has no
 * platform-wide dashboard to show — their landing page is their own company.
 */
function RoleHome() {
  const role = useAuthStore((s) => s.user?.role)
  return role === 'COMPANY_ADMIN' ? <CompanyPortal /> : <Dashboard />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  useSessionTimeout()

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleHome />} />
        <Route path="company" element={<RoleRoute path="/company"><CompanyPortal /></RoleRoute>} />
        <Route path="users" element={<RoleRoute path="/users"><Users /></RoleRoute>} />
        <Route path="map" element={<RoleRoute path="/map"><MapView /></RoleRoute>} />
        <Route path="pickups" element={<RoleRoute path="/pickups"><Pickups /></RoleRoute>} />
        <Route path="assignments" element={<RoleRoute path="/assignments"><Assignments /></RoleRoute>} />
        <Route path="reports" element={<RoleRoute path="/reports"><Reports /></RoleRoute>} />
        <Route path="analytics" element={<RoleRoute path="/analytics"><Analytics /></RoleRoute>} />
        <Route path="collectors" element={<RoleRoute path="/collectors"><Collectors /></RoleRoute>} />
        <Route path="companies" element={<RoleRoute path="/companies"><Companies /></RoleRoute>} />
        <Route path="payments" element={<RoleRoute path="/payments"><Payments /></RoleRoute>} />
        <Route path="donations" element={<RoleRoute path="/donations"><Donations /></RoleRoute>} />
        <Route path="payouts" element={<RoleRoute path="/payouts"><Payouts /></RoleRoute>} />
        <Route path="subscriptions" element={<RoleRoute path="/subscriptions"><Subscriptions /></RoleRoute>} />
        <Route path="ratings" element={<RoleRoute path="/ratings"><Ratings /></RoleRoute>} />
        <Route path="support" element={<RoleRoute path="/support"><Support /></RoleRoute>} />
        <Route path="notifications" element={<RoleRoute path="/notifications"><Notifications /></RoleRoute>} />
        <Route path="audit-log" element={<RoleRoute path="/audit-log"><AuditLog /></RoleRoute>} />
        <Route path="system-health" element={<RoleRoute path="/system-health"><SystemHealth /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute path="/settings"><Settings /></RoleRoute>} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
